import React, { useCallback, useEffect, useState } from 'react';
import { Trans } from '@lingui/macro';

import { Box, Button, Flex, FlexProps, Text } from '@rover/kibble/core';
import { Props as ButtonProps } from '@rover/kibble/core/Button';
import { ZIndex } from '@rover/kibble/styles';
import { useTheme } from '@rover/kibble/themes';
import useLocationContext from '@rover/react-lib/src/context/useLocationContext';
import usePageContext from '@rover/react-lib/src/context/usePageContext';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';

export const AdminButton = (props: ButtonProps): JSX.Element => {
  return <Button variant="primary" size="small" mx="1x" {...props} />;
};

export const AdminButtonSecondary = (props: ButtonProps): JSX.Element => {
  return <Button contrast size="small" mx="1x" {...props} />;
};

const DropdownMenuItem = (props: FlexProps): JSX.Element => {
  return (
    <Flex
      py="2x"
      px="3x"
      width="100%"
      sx={{
        whiteSpace: 'nowrap',
        color: 'white !important',
        ...(props.as === 'a' ? { cursor: 'pointer' } : {}),
      }}
      {...props}
    />
  );
};

const AdminDropdown = ({
  open,
  team,
  hideUrl,
  useExtendedDropdown,
  peopleUrl,
}: {
  hideUrl: string;
  team: string;
  peopleUrl?: string;
  open: boolean;
  useExtendedDropdown: boolean;
}): JSX.Element => {
  const { colors } = useTheme();
  return (
    <Flex
      alignItems="center"
      backgroundColor={colors.neutral[800].toString()}
      borderRadius="secondary"
      display={open ? 'initial' : 'none'}
      flexDirection="column"
      position="absolute"
      right="8px"
      top="56px"
      zIndex={ZIndex.MODAL.toString()}
    >
      <Flex flexDirection="column" m="0x" p="0x">
        <DropdownMenuItem>
          <Text size="100" fontWeight="semibold">
            <Trans>Page Owner:</Trans>&nbsp;
          </Text>
          {team}
        </DropdownMenuItem>
        {useExtendedDropdown && (
          <>
            <DropdownMenuItem as="a" href="/admin/">
              <Trans>Admin</Trans>
            </DropdownMenuItem>
            <DropdownMenuItem as="a" href={peopleUrl}>
              <Trans>People</Trans>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem as="a" href={hideUrl}>
          <Trans>Hide Toolbar</Trans>
        </DropdownMenuItem>
      </Flex>
    </Flex>
  );
};

const AdminToolbar = ({ children }: { children?: React.ReactNode }): JSX.Element => {
  const { colors } = useTheme();
  const { isImpersonating, user, peopleUrl } = useCurrentUserShim();
  const [open, setOpen] = useState(false);
  const closeDropdown = useCallback(() => setOpen(false), [setOpen]);
  const handleToggle = useCallback(() => {
    setOpen(!open);
    if (!open) {
      setTimeout(() => {
        // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
        document.addEventListener('click', closeDropdown, false);
      }, 0);
    }
  }, [open, setOpen, closeDropdown]);

  const { concept } = usePageContext();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [subconcept, team] = concept;

  const formattedTeam = team
    .split('_')
    .map((word) => (word === 'and' ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');

  const location = useLocationContext();
  const { pathname } = location;

  const hideUrl = `/admin/people/person/disable-header-toolbar/?next=${pathname}`;
  const stopUrl = `/people/stop-impersonating-user/?next=${pathname}`;

  const useExtendedDropdown = Boolean(React.Children.count(children));

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
      document.removeEventListener('click', closeDropdown);
    }
  }, [open, setOpen, closeDropdown]);

  return (
    <Flex
      width="100%"
      backgroundColor={colors.neutral[800].toString()}
      py="3x"
      px="6x"
      justifyContent="space-between"
      display={['none', 'flex']}
    >
      <Flex alignItems="center" justifyContent="start">
        {isImpersonating && (
          <Text color="white" size="200" id="Impersonating">
            <Trans>Impersonating</Trans>
          </Text>
        )}
        {!isImpersonating && (
          <Text color="white" size="200" id="Welcome">
            <Trans>Welcome</Trans>
          </Text>
        )}{' '}
        <Text color="white" pl="2x" fontWeight="semibold" size="200">
          {user?.firstName} {user?.lastName}
        </Text>
      </Flex>
      <Flex flexDirection="row" justifyContent="end" alignItems="center">
        {children}
        <Flex flexDirection="row">
          {isImpersonating && (
            <AdminButtonSecondary href={stopUrl}>
              <Trans>Stop Impersonating</Trans>
            </AdminButtonSecondary>
          )}
          {!useExtendedDropdown && (
            <>
              <AdminButtonSecondary href="/admin/">
                <Trans>Admin</Trans>
              </AdminButtonSecondary>
              <AdminButtonSecondary href={peopleUrl}>
                <Trans>People</Trans>
              </AdminButtonSecondary>
            </>
          )}
          <Box position="relative" ml="1x">
            <AdminButtonSecondary onClick={handleToggle}>More</AdminButtonSecondary>
            <AdminDropdown
              open={open}
              team={formattedTeam}
              hideUrl={hideUrl}
              useExtendedDropdown={useExtendedDropdown}
              peopleUrl={peopleUrl}
            />
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default AdminToolbar;
